import React, {useEffect, useRef, useState} from 'react';
import {useRestaurantOnboardingForm} from '../hooks/useRestaurantOnboardingForm';
import {storeService} from '../services/api';
import RestaurantStepper from '../components/onboarding/restaurant/RestaurantStepper';
import StepRestaurantWelcome from '../components/onboarding/restaurant/StepRestaurantWelcome';
import StepRestaurantAuth from '../components/onboarding/restaurant/StepRestaurantAuth';
import StepRestaurantBasicInfo from '../components/onboarding/restaurant/StepRestaurantBasicInfo';
import StepRestaurantCategory from '../components/onboarding/restaurant/StepRestaurantCategory';
import StepRestaurantContactInfo from '../components/onboarding/restaurant/StepRestaurantContactInfo';
import StepRestaurantCity from '../components/onboarding/restaurant/StepRestaurantCity';
import StepRestaurantLocation from '../components/onboarding/restaurant/StepRestaurantLocation';
import StepRestaurantBank from '../components/onboarding/restaurant/StepRestaurantBank';
import StepRestaurantBilling from '../components/onboarding/restaurant/StepRestaurantBilling';
import StepRestaurantPhoto from '../components/onboarding/restaurant/StepRestaurantPhoto';
import StepRestaurantPlan from '../components/onboarding/restaurant/StepRestaurantPlan';
import StepRestaurantSchedule from '../components/onboarding/restaurant/StepRestaurantSchedule';
import StepRestaurantTerms from '../components/onboarding/restaurant/StepRestaurantTerms';
import StepRestaurantSuccess from '../components/onboarding/restaurant/StepRestaurantSuccess';
import './RestaurantOnboarding.css';

// 0=Bienvenida 1=Identidad 2=Imagen 3=Categoría 4=Contacto 5=Ciudad 6=Dirección 7=Plan 8=Facturación 9=Banco 10=Horario 11=Términos 12=Éxito
const TOTAL = 13;

const RestaurantOnboarding = () => {
    const {
        currentStep,
        formData,
        updateFormData,
        nextStep,
        prevStep,
        resetForm,
    } = useRestaurantOnboardingForm();

    const [errors, setErrors] = useState({});
    const [stepKey, setStepKey] = useState(0);
    const [slideDir, setSlideDir] = useState('fwd');
    const [isStepping, setIsStepping] = useState(false);
    const [btnErr, setBtnErr] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [showAuth, setShowAuth] = useState(false);
    const footerRef = useRef(null);
    const countWords = (text = '') =>
        text
            .trim()
            .split(/\s+/)
            .filter(word => word.length > 0)
            .length;


    /* ── Alto real del footer → --ro-footer-h (la barra "Seleccionado" del paso Categoría se apoya encima, sin tapar Siguiente) ── */
    useEffect(() => {
        const el = footerRef.current;
        if (!el) return undefined;
        const setH = () => document.documentElement.style.setProperty('--ro-footer-h', `${el.offsetHeight}px`);
        setH();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(setH);
        ro.observe(el);
        return () => ro.disconnect();
    });

    /* ── Centralised validation per step ── */
    const validateStep = () => {
        const e = {};

        if (currentStep === 1) {
            if (!formData.name?.trim()) {
                e.name = 'El nombre del negocio es obligatorio';
            }

            const description = formData.description?.trim() || '';
            const words = countWords(description);

            if (!description) {
                e.description = 'La descripción del negocio es obligatoria';
            } else if (words < 8) {
                e.description = 'La descripción debe tener al menos 8 palabras';
            } else if (words > 15) {
                e.description = 'La descripción no puede superar 15 palabras';
            }

            if (!formData.estimated_time)
                e.estimated_time = 'Selecciona el tiempo estimado de entrega';
        }


        if (currentStep === 2) {
            if (!formData.photo)
                e.photo = 'El logotipo es obligatorio para continuar.';
        }

        if (currentStep === 3) {
            if (!formData.category_id)
                e.category_id = 'Selecciona una categoría';
            else if (!formData.subcategory_id)
                e.subcategory_id = 'Selecciona una especialidad';
        }

        if (currentStep === 4) {
            if (!formData.contact_email?.trim()) {
                e.contact_email = 'El email de contacto es obligatorio';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
                e.contact_email = 'Ingresa un email válido';
            }
            const mobile = (formData.mobile || '').replace(/\D/g, '').replace(/^0+/, '');
            if (!mobile) {
                e.mobile = 'El celular es obligatorio';
            } else if (mobile.length !== 9) {
                e.mobile = 'El celular debe tener 9 dígitos (ej. 991234567)';
            } else if (!/^\d{9}$/.test(mobile)) {
                e.mobile = 'El celular solo debe contener números';
            }
        }

        if (currentStep === 5) {
            if (!formData.city_id)
                e.city_id = 'Selecciona una ciudad para continuar';
        }

        if (currentStep === 6) {
            if (!formData.latitude || !formData.longitude)
                e.address = 'Por favor sitúa el pin en el mapa para continuar';
            else if (formData.out_of_range)
                e.address = 'Esta ubicación está fuera de nuestra zona de cobertura';
            if (!formData.address_main?.trim())
                e.address_main = 'La calle principal es obligatoria';
            if (!formData.address_cross?.trim())
                e.address_cross = 'La calle secundaria es obligatoria';
            if (!formData.address_ref?.trim())
                e.address_ref = 'La referencia es obligatoria';
        }

        if (currentStep === 7) {
            if (formData.exclusive === null || formData.exclusive === undefined)
                e.exclusive = 'Selecciona un plan para continuar';
        }

        if (currentStep === 8) {
            if (!formData.business_name?.trim())
                e.business_name = 'La razón social es obligatoria';
            if (!formData.ruc?.trim())
                e.ruc = 'El RUC es obligatorio';
            else if (formData.ruc.length !== 13)
                e.ruc = 'El RUC debe tener 13 dígitos';
            if (!formData.billing_address?.trim())
                e.billing_address = 'La dirección de facturación es obligatoria';
            if (!formData.representative_name?.trim())
                e.representative_name = 'El nombre del representante es obligatorio';
            if (!formData.representative_last_name?.trim())
                e.representative_last_name = 'El apellido del representante es obligatorio';
            if (!formData.representative_dni?.trim())
                e.representative_dni = 'La cédula es obligatoria';
            else if (formData.representative_dni.length !== 10)
                e.representative_dni = 'La cédula debe tener 10 dígitos';
            if (!formData.ruc_certificate)
                e.ruc_certificate = 'El certificado de RUC activo es obligatorio';
        }

        if (currentStep === 9) {
            if (!formData.bank_id)
                e.bank_id = 'Selecciona tu banco';
            if (!formData.bank_account_type)
                e.bank_account_type = 'Selecciona el tipo de cuenta';
            if (!formData.bank_account_number?.trim())
                e.bank_account_number = 'El número de cuenta es obligatorio';
            if (!formData.bank_owner_name?.trim())
                e.bank_owner_name = 'El nombre del titular es obligatorio';
            if (!formData.bank_ci_number?.trim()) {
                e.bank_ci_number = 'El número de documento es obligatorio';
            } else if (formData.bank_ci_type === 'CI' && formData.bank_ci_number.length !== 10) {
                e.bank_ci_number = 'La cédula debe tener 10 dígitos';
            } else if (formData.bank_ci_type === 'RUC' && formData.bank_ci_number.length !== 13) {
                e.bank_ci_number = 'El RUC debe tener 13 dígitos';
            } else if (formData.bank_ci_type === 'PASAPORTE' && formData.bank_ci_number.length < 6) {
                e.bank_ci_number = 'El pasaporte debe tener al menos 6 caracteres';
            }
        }

        if (currentStep === 10) {
            const schedule = formData.schedule;
            if (!schedule) {
                e.schedule = 'Configura al menos un día de atención';
            } else {
                const toMin = (s) => { const [h, m] = s.split(':').map(Number); return h * 60 + (m || 0); };
                const cityHours = formData.city_operating_hours || [];
                let badShift = false;
                let cityViolation = false;
                let totalMinutes = 0;
                for (const part of schedule.split(';').filter(Boolean)) {
                    const [dayKey, start, end] = part.split(',');
                    const startMin = toMin(start);
                    const endMin   = toMin(end);
                    if (endMin <= startMin) { badShift = true; break; }
                    totalMinutes += (endMin - startMin);
                    // Validar contra horario de cobertura de la ciudad
                    const cityDay = cityHours.find((h) => h.day === dayKey);
                    if (cityDay) {
                        if (endMin > toMin(cityDay.end) || startMin < toMin(cityDay.start)) {
                            cityViolation = true;
                        }
                    }
                }
                if (badShift) {
                    e.schedule = 'Uno o más turnos son inválidos: la hora de cierre debe ser posterior a la apertura';
                } else if (cityViolation) {
                    e.schedule = 'Uno o más días tienen horarios fuera del rango de cobertura de tu ciudad. Corrígelos antes de continuar.';
                } else {
                    const minMinutes = (formData.city_min_hours || 0) * 60;
                    if (minMinutes > 0 && totalMinutes < minMinutes) {
                        const h = formData.city_min_hours;
                        const totalH = +(totalMinutes / 60).toFixed(1);
                        e.schedule = `Tu negocio debe operar al menos ${h}h a la semana. Llevas ${totalH}h configuradas.`;
                    }
                }
            }
        }

        if (currentStep === 11) {
            if (!formData.terms_accepted)
                e.terms_accepted = 'Debes aceptar los términos y condiciones para continuar';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const clearError = (field) =>
        setErrors((prev) => ({...prev, [field]: null}));

    /* ── Auth gate ── */
    const handleAuthComplete = (email, mobile) => {
        updateFormData({ contact_email: email, ...(mobile ? { mobile } : {}) });
        setShowAuth(false);
        setSlideDir('fwd');
        setStepKey((k) => k + 1);
        nextStep();
    };

    /* ── Navigation ── */
    const handleNext = () => {
        if (isStepping) return;
        // Step 0 (welcome) → open auth gate instead of going straight to step 1
        if (currentStep === 0) {
            setShowAuth(true);
            return;
        }
        if (validateStep()) {
            setErrors({});
            setBtnErr(false);
            setIsStepping(true);
            setTimeout(() => {
                setIsStepping(false);
                setSlideDir('fwd');
                setStepKey((k) => k + 1);
                nextStep();
            }, 420);
        } else {
            setBtnErr(true);
            setTimeout(() => setBtnErr(false), 2000);
        }
    };

    const handleBack = () => {
        setErrors({});
        setSlideDir('back');
        setStepKey((k) => k + 1);
        prevStep();
    };

    /* ── Submit: crear restaurante con el token obtenido en auth ── */
    const handleSubmit = async () => {
        if (isSubmitting) return;
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            await storeService.create(formData);

            // Avanzar a la pantalla de éxito
            setSlideDir('fwd');
            setStepKey((k) => k + 1);
            nextStep();
        } catch (err) {
            const data = err?.response?.data;
            const msg = data?.error || data?.message
                || (err?.response ? 'No pudimos completar tu registro. Inténtalo de nuevo en unos minutos.'
                                  : 'Error de conexión. Revisa tu internet e inténtalo de nuevo.');
            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ── Step renderer ── */
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <StepRestaurantWelcome onNext={handleNext}/>;
            case 1:
                return (
                    <StepRestaurantBasicInfo
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 2:
                return (
                    <StepRestaurantPhoto
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 3:
                return (
                    <StepRestaurantCategory
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 4:
                return (
                    <StepRestaurantContactInfo
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 5:
                return (
                    <StepRestaurantCity
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 6:
                return (
                    <StepRestaurantLocation
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 7:
                return (
                    <StepRestaurantPlan
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 8:
                return (
                    <StepRestaurantBilling
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 9:
                return (
                    <StepRestaurantBank
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 10:
                return (
                    <StepRestaurantSchedule
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 11:
                return (
                    <StepRestaurantTerms
                        formData={formData}
                        onUpdate={updateFormData}
                        errors={errors}
                        onClearError={clearError}
                    />
                );
            case 12:
                return <StepRestaurantSuccess onReset={resetForm} />;
            default:
                return null;
        }
    };


    const isLastStep = currentStep === TOTAL - 2;
    const isSuccessStep = currentStep === TOTAL - 1;
    const showFooter = currentStep > 0 && !isSuccessStep;
    const isCatStep = currentStep === 3;

    return (
        <div className="ro-page">

            {/* ── Header ── */}
            <header className="ro-header">
                <div className="ro-header-inner">
                    <img src="/logo_jama2.png" alt="JAMA Delivery" className="ro-header-logo"/>
                    <span className="ro-header-title">Registremos mi negocio</span>
                    <a href="https://wa.me/593981026880" target="_blank" rel="noopener noreferrer" className="ro-header-help">
                        ¿Necesitas ayuda?
                    </a>
                </div>
            </header>

            {/* ── Auth gate (overlay, step 0 → "Comenzar registro") ── */}
            {showAuth && (
                <StepRestaurantAuth
                    onAuthComplete={handleAuthComplete}
                    onBack={() => setShowAuth(false)}
                />
            )}

            {/* ── Progress bar ── */}
            {currentStep > 0 && !isSuccessStep && !showAuth && (
                <div className="ro-stepper-wrap">
                    <RestaurantStepper currentStep={currentStep} total={TOTAL}/>
                </div>
            )}

            {/* ── Content card — pastel bg on category step ── */}
            {!showAuth && (isSuccessStep || currentStep === 0 ? (
                <div key={stepKey} className={`ro-step-anim-${slideDir}`}>
                    {renderStep()}
                </div>
            ) : (
                <div className={`ro-card${isCatStep ? ' ro-card--cat' : ''}`}>
                    <div key={stepKey} className={`ro-step-anim-${slideDir}`}>
                        {renderStep()}
                    </div>
                </div>
            ))}

            {/* ── Sticky footer nav (hidden on welcome) ── */}
            {showFooter && !showAuth && (
                <footer className="ro-footer" ref={footerRef}>
                    <div className="ro-footer-inner">
                        <button className="ro-btn-back" onClick={handleBack}>
                            <span className="ro-arr">←</span> Atrás
                        </button>

                        {isLastStep ? (
                            <>
                                {submitError && (
                                    <span className="ro-submit-error">{submitError}</span>
                                )}
                                <button
                                    className={`ro-btn-next ro-btn-submit${isSubmitting ? ' ro-btn-next--ok' : ''}`}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? <><span className="ro-spin ro-spin--sm" /> Enviando...</>
                                        : <>✓ Enviar negocio</>
                                    }
                                </button>
                            </>
                        ) : (
                            <button
                                className={`ro-btn-next${isStepping ? ' ro-btn-next--ok' : ''}${btnErr ? ' ro-btn-next--err' : ''}`}
                                onClick={handleNext}
                                disabled={isStepping}
                            >
                                {isStepping ? (
                                    <><span className="ro-btn-ok-ico">✓</span> ¡Listo!</>
                                ) : btnErr ? (
                                    <>⚠ Revisa los campos</>
                                ) : (
                                    <>Siguiente <span className="ro-arr">→</span></>
                                )}
                            </button>
                        )}
                    </div>
                </footer>
            )}

        </div>
    );
};

export default RestaurantOnboarding;
